import { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';

export interface WaveformProps {
  isRecording?: boolean;
  isPaused?: boolean;
  color?: string;
  barCount?: number;
  data?: number[]; // Array of values from 0 to 1 representing volume/pitch
  minHeight?: number;
}

export const Waveform: FC<WaveformProps> = ({
  isRecording = false,
  isPaused = false,
  color = 'var(--md-sys-color-primary)',
  barCount = 40,
  data,
  minHeight = 12,
}) => {
  // Generate random heights for the bars to simulate a waveform
  const bars = useMemo(
    () => Array.from({ length: barCount }, (_, i) => i),
    [barCount],
  );

  const getBarHeight = (index: number) => {
    if (!isRecording || isPaused) return minHeight;

    // Use actual audio data if provided
    if (data && data.length > 0) {
      const dataIndex = Math.floor((index / barCount) * data.length);
      const value = data[dataIndex] || 0;
      return Math.max(minHeight, value * 120 + minHeight);
    }

    // Otherwise, use a more dynamic simulated animation even when "idle"
    // but at a lower amplitude. Using a deterministic sine wave to avoid Math.random() for SonarCloud.
    if (isRecording && !isPaused) {
      // Create a complex organic movement using multiple sine waves
      const variance = Math.sin(index * 1.5) * 40 + Math.cos(index * 0.7) * 20;
      return [minHeight, minHeight + 60 + variance, minHeight];
    }

    // Resting state: a very subtle movement to show it's "ready"
    return [minHeight, minHeight + (index % 3) * 4, minHeight];
  };

  return (
    <StyledWaveformWrapper>
      <StyledWaveformContainer>
        {bars.map((i) => (
          <StyledBar
            key={i}
            animate={{
              height: getBarHeight(i),
            }}
            transition={{
              duration: data ? 0.1 : 0.6,
              repeat: data ? 0 : Infinity,
              ease: 'easeInOut',
              delay: data ? 0 : (i % 10) * 0.05,
            }}
            style={{
              backgroundColor: color,
              opacity: isRecording && !isPaused ? 1 : 0.3,
            }}
          />
        ))}
      </StyledWaveformContainer>
    </StyledWaveformWrapper>
  );
};

const StyledWaveformWrapper = styled.div`
  width: 100%;
  background: var(--md-sys-color-surface-container-low);
  border-radius: 24px;
  padding: 32px 16px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--md-sys-color-outline-variant);
`;

const StyledWaveformContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 140px;
  width: 100%;
  overflow: hidden;

  ${mq(BreakpointSizes.desktop)} {
    gap: 6px;
  }
`;

const StyledBar = styled(motion.div)`
  width: 5px;
  border-radius: 4px;
  min-height: 12px;
  transition:
    opacity 0.3s ease,
    background-color 0.3s ease;

  ${mq(BreakpointSizes.desktop)} {
    width: 8px;
  }
`;
